import { FrankieOneVerificationStatus } from '@/core/enums/FrankieOneVerificationStatus';
import { useAuthStore } from '@/modules/auth/store/auth';
import type { ProfileType } from '@/types/FrankieOne';
import { BIOMETRIC_ONLY, GOV_ID_BIOMETRIC, GOV_ID_ONLY } from '@/utils/enums/FrankieOne';
import type { OneSdkContext } from '@frankieone/one-sdk';
import { storeToRefs } from 'pinia';
import { nextTick } from 'vue';

export default function useFrankieOne() {
  const route = useRoute();
  const router = useRouter();
  const config = useRuntimeConfig();

  const authStore = useAuthStore();
  const { user, isClient } = storeToRefs(authStore);
  const { getDocument } = authStore;

  const fetching = ref(false);
  const loading = ref(true);
  const verifying = ref(false);
  const showProcessingSplash = ref(false);

  const oneSdk = ref<OneSdkContext>();
  const oneSdkIndividual = ref();
  const frankieOneStatus = ref<string | null>(null);
  const frankieOneSession = ref();
  const idvEntityId = ref<string | null>(null);

  const { showSnackbar } = useSnackbar();

  const isVerificationPassed = computed((): boolean => {
    return frankieOneStatus.value === FrankieOneVerificationStatus.PASS;
  });
  const isVerificationFailed = computed((): boolean => {
    return frankieOneStatus.value === FrankieOneVerificationStatus.FAIL;
  });
  const isVerificationRefer = computed((): boolean => {
    return frankieOneStatus.value === FrankieOneVerificationStatus.REFER;
  });
  const isVerificationUnchecked = computed((): boolean => {
    return frankieOneStatus.value === FrankieOneVerificationStatus.UNCHECKED;
  });
  const isVerificationNone = computed((): boolean => {
    return frankieOneStatus.value === FrankieOneVerificationStatus.NONE;
  });

  const getFrankieOneStatus = async (): Promise<void> => {
    if (fetching.value) return;

    fetching.value = true;
    frankieOneStatus.value = null;

    const { data, error } = await useFetchData('frankieone/entity-check' as any);

    if ((data.value as any)?.data) {
      frankieOneStatus.value = (data.value as any).data.action_recommended;
    } else if (error.value) {
      showSnackbar({
        text: error.value,
        state: false,
      });
    }

    fetching.value = false;
  };

  const getFrankieOneSession = async (): Promise<void> => {
    if (fetching.value) return;

    fetching.value = true;
    frankieOneSession.value = null;

    const { data, error, execute } = useSubmit();
    // if(error) {
    //   showSnackbar({
    //     text: error.value,
    //     state: true,
    //   });
    //   router.push('/my-profile/profile-info/identity-documents');
    //   // emits('error');
    // }
    await execute('frankieone/machine-session', {
      method: 'POST',
    });

    if ((data.value as any)?.data) {
      frankieOneSession.value = (data.value as any).data.session_object.token;
      console.log('frankieOneSession.value: ', frankieOneSession.value);
    } else if (error.value) {
      back();
    }

    fetching.value = false;
  };

  const getFrankieOneFinalStatus = async (): Promise<void> => {
    if (fetching.value) return;

    fetching.value = true;
    frankieOneStatus.value = null;

    const { data, error } = await useFetchData('frankieone/entity/status' as any);
    if (data.value) {
      if(data.value.entity_profile_result === 'PASS') {
        router.push('/my-profile/profile-info');
        showSnackbar({
          text: 'Your ID verification has been successfully verified.',
          state: 'success',
        });
      } else if(data.value.entity_profile_result === 'FAIL') {
        // Show splash before informing user
        verifying.value = false;
        fetching.value = false;
        showProcessingSplash.value = true;
        await nextTick();
        await new Promise((r) => setTimeout(r, 2000));
        showProcessingSplash.value = false;
        router.push('/my-profile/profile-info');
        showSnackbar({
          text: data.value.failed_reason ?  data.value.failed_reason : 'Your ID verification is in process. Please check after some time and try again if fail.',
          state: 'success',
        });
      } else if(data.value.entity_profile_result === 'UNCHECKED') {
        // Show splash before informing user
        verifying.value = false;
        fetching.value = false;
        showProcessingSplash.value = true;
        await nextTick();
        await new Promise((r) => setTimeout(r, 2000));
        showProcessingSplash.value = false;
        router.push('/my-profile/profile-info');
        showSnackbar({
          text: 'Your ID verification is in process. Please check after some time and try again if fail.',
          state: 'success',
        }); 
      }
    } else if (error.value) {
      console.log('error.value: ', error);
      showSnackbar({
        text: error.value,
        state: false,
      });
    }

    fetching.value = false;
  };

  const initializeFrankieOne = async (
    elementId: string,
    profileType: ProfileType = GOV_ID_BIOMETRIC
  ): Promise<void> => {
    if (!frankieOneSession.value) {
      fail('FrankieOne: Failure to initialize due to missing session token');
      return;
    }
    try {
      const FrankieOne = await import('@frankieone/one-sdk');
      const isDummy = false;
      console.log('isDummy: ', isDummy);
      const oneSdkOptions: any = {
        mode: isDummy
          ? { modeName: 'dummy', mocks: {} }
          : { modeName: 'production' },
        ...(isDummy ? {} : { session: { token: frankieOneSession.value } }),
        recipe: {
          form: {
            provider: {
              name: 'react',
              googleApiKey: config.public.googleApiKey,
            },
          },
          // In dummy mode, provide minimal IDV config (no provider) to avoid vendor SDK load
          idv: isDummy
            ? {
                documents: [
                  { type: 'PASSPORT', countries: ['AUS'] },
                ],
              }
            : undefined,
        },
      };
      oneSdk.value = await FrankieOne.OneSDK(oneSdkOptions);
      oneSdkIndividual.value = oneSdk.value.individual();
      oneSdkIndividual.value.setProfileType('auto');

      const appSelector = `#${elementId}`;
      const appContainer = document.getElementById(elementId);
      // if (!appContainer) {
      //   fail('FrankieOne: Failure to initialize due to missing container');
      //   return;
      // }

      const consent = oneSdk.value.component('form', {
        name: 'CONSENT',
        mode: 'individual',
        type: 'manual',
        descriptions: [
          {
            label: `Your personal information will be collected and used in accordance with SupportApp's Privacy Policy.`,
            style: {
              'ff-description': {
                marginBottom: '20px',
              },
            },
          },
          {
            label: `In addition, for the purposes of verifying your identity, you consent to:`,
            style: {},
          },
          {
            label: `1) the verification of your personal information with credit bureau header files (for verification only);`,
            style: {},
          },
          {
            label: `2) against records held by official document issuers or official record holders via third party systems;`,
            style: {},
          },
          {
            label: `3) verification agent(s) acting as a nominated intermediary in accordance with Australian Privacy Principles.`,
            style: {},
          },
          {
            label: `You consent to the use by third parties of the results of any verification checks on Your identity for the purposes of monitoring and improving their verification services.`,
            style: {},
          },
        ],
      });

      const idvFlow: any = oneSdk.value!.flow('idv');
      const idvStatus = idvFlow.statuses;

      const startIdv = (): void => {
        showLoading();
        idvFlow.mount(appSelector);
      };

      const startConsent = (): void => {
        showLoading();
        setVerifying(false);
        consent.mount(appSelector);
      };

      startConsent();

      consent.on('form:consent:loaded', async () => {
        hideLoading();
      });
      consent.on('form:consent:ready', async () => {
        // Removing the addConsent as suggested by FrankieOne team
        // oneSdkIndividual.value.addConsent('idv');
        startIdv();
      });

      idvFlow.on('ready', () => {
        hideLoading();
        setVerifying(true);
      });
      idvFlow.on('detection_complete', () => {
        unmount(elementId);
      });
      idvFlow.on('results', async ({ checkStatus, document, entityId }: any) => {
        hideLoading();
        console.log('checkStatus: ', checkStatus);
        idvEntityId.value = entityId || idvEntityId.value || null;
        if (checkStatus === idvStatus.COMPLETE) {
          verify();
          getFrankieOneFinalStatus();
        }
        if (checkStatus === idvStatus.FAILED) {
          console.log('checkStatus === idvStatus.FAILED: ');
          fail();
        }
      });
      idvFlow.on('input_required', ({ entityId }: any, checkStatus: any) => {
        hideLoading();
        startIdv();
      });
      idvFlow.on('error', ({ message, payload }: any) => {
        const isMissingConsent = payload?.errorStatus === idvStatus.AWAITING_CONSENT;
        if (isMissingConsent) {
          console.log('if isMissingConsent: ');
          startConsent();
        } else {
          console.log('else {: ');
          hideLoading();
          fail(message);
        }
      });
    } catch (e: any) {
      console.log('catch (e: any)');
      fail(e?.message || 'FrankieOne: Initialization error');
    }
  };

  // Removed unused KYC/legacy flows in favor of IDV

  const unmount = (elementId: string): void => {
    const el = document.getElementById(`${elementId}`);
    if (el) el.outerHTML = '';
  };

  const verify = async (): Promise<void> => {
    try {
      const checkSummary = await oneSdkIndividual.value.submit({ verify: true } as any);
      console.log('checkSummary: ', checkSummary);
      const checkStatus = checkSummary?.status?.key;

      const isPassed = checkStatus === 'PASSED';
      // Push to backend webhook regardless of pass/refer/fail so backend is aware
      try {
        const requestId = (idvEntityId.value || checkSummary?.entityId || '').toString();
        // const requestId = frankieOneSession.value;
        if (requestId) {
          await useFetchData(`/webhook/frankieone/${requestId}`, {
            method: 'POST',
            body: { status: checkStatus, entityId: requestId },
          } as any);
          // alert('webhook/frankieone/${requestId}');
        }
      } catch (_) {}
      if (isPassed || checkStatus === 'REFER') {
        await getFrankieOneStatus();

        if (isPassed) {
          await approveIdDocumentVerification();
        }

        showSnackbar({
          text: `Verification ${isPassed ? 'complete' : 'pending'}!`,
        });

        // back(true);
      }

      if (checkSummary.status.key === 'FAIL') {
        fail();
      }
    } catch (e) {
      fail('Something went wrong, cannot verify details');
    }
  };

  const fail = (message: string = 'Verification failed!'): void => {
    console.log(' FrankieOne Verification failed message: ', message);
    // back(false);
  };

  const back = (success: boolean = true): void => {
    const redirect: { name: string; params: Record<string, any>; query: Record<string, any> } = {
      name: '',
      params: {},
      query: {
        from: 'verification',
      },
    };

    if (route.query?.from === 'profile') {
      console.log('route.query?.from: ', route.query?.from);
      // if (isClient.value) {
      //   redirect.name = 'profile-profile-information-identity-documents';
      // } else {
      //   redirect.name = 'profile-profile-information-required-documents-document';
      //   redirect.params.document = 'id-verification';
      // }
    } else {
      // redirect.name = 'onboarding';
      // redirect.query.form = success
      //   ? isClient.value
      //     ? 'support-requirements'
      //     : 'required-documents'
      //   : 'id-verification';
    }

    router.push(redirect);
  };

  const showLoading = (): void => {
    return setLoading(true);
  };

  const hideLoading = (): void => {
    return setLoading(false);
  };

  const setLoading = (bool: boolean): void => {
    loading.value = bool;
  };

  const setVerifying = (bool: boolean): void => {
    verifying.value = bool;
  };

  const approveIdDocumentVerification = async (): Promise<void> => {
    // @NOTE: Refresh `/me` to get latest documents data obj.
    await reloadNuxtApp();
  };

  return {
    fetching,
    loading,
    verifying,
    showProcessingSplash,

    oneSdk,
    frankieOneStatus,
    frankieOneSession,

    isVerificationPassed,
    isVerificationFailed,
    isVerificationRefer,
    isVerificationUnchecked,
    isVerificationNone,

    getFrankieOneStatus,
    getFrankieOneSession,
    initializeFrankieOne,
    getFrankieOneFinalStatus,
  };
}
